import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import DocumentNameSchema from "documents/names/_schema/document-name-schema";
import DocumentTopicSchema from "documents/topics/_schema/document-topic-schema";
import { DocumentFileType } from "documents/types/document-file-type";
import { z } from "zod";

export type DocumentJSON = z.infer<typeof DocumentSchema.DocumentJSONZ>;
export type DocumentParams = z.infer<typeof DocumentSchema.DocumentZ>;

export default class DocumentSchema {
  public static readonly DocumentJSONZ = z.object({
    id: z.uuid(),
    creatorId: z.uuid().nullish(),
    fileSize: z.number().nullish(),
    fileType: z.string().refine((val) => Object.values(DocumentFileType).includes(val as DocumentFileType)),
    created: z.iso.datetime({ offset: true }).nullish(),
    readAccess: z.array(z.uuid()).nullish(),
    writeAccess: z.array(z.uuid()).nullish(),
    hasSystemPermissions: z.boolean().nullish(),
    name: z.string(),
    topics: z.array(DocumentTopicSchema.DocumentTopicJSONZ).nullish(),
    templateIds: z.array(z.uuid()).nullish(),
  });
  public static readonly DocumentZ = z.object({
    id: z.instanceof(Guid),
    creatorId: z.instanceof(Guid).optional(),
    fileSize: z.number().optional(),
    fileType: z.enum(DocumentFileType),
    created: AHDateSchema.type.optional(),
    readAccess: z.array(z.instanceof(Guid)).optional(),
    writeAccess: z.array(z.instanceof(Guid)).optional(),
    hasSystemPermissions: z.boolean().optional(),
    name: DocumentNameSchema.DocumentNameZ,
    topics: z.array(DocumentTopicSchema.DocumentTopicZ).optional(),
    templateIds: z.array(z.instanceof(Guid)).optional(),
    replacesId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): DocumentParams {
    return parseOrThrow(DocumentSchema.DocumentZ, values, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DocumentJSON {
    return parseOrThrow(DocumentSchema.DocumentJSONZ, values, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DocumentParams {
    try {
      DocumentSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DocumentJSON {
    try {
      DocumentSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): DocumentParams {
    const parsed = parseOrThrow(DocumentSchema.DocumentJSONZ, json, {
      logError: true,
      schemaName: "DocumentSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return DocumentSchema.codec.decode(parsed);
  }

  public static serialize(document: DocumentParams): DocumentJSON {
    const parsed = DocumentSchema.validate(document);
    return DocumentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(DocumentSchema.DocumentJSONZ, DocumentSchema.DocumentZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      creatorId: Guid.fromJSON(data.creatorId) ?? undefined,
      fileSize: data.fileSize ?? undefined,
      fileType: DocumentFileType[enumKeyOf(DocumentFileType, data.fileType) as keyof typeof DocumentFileType],
      created: AHDate.fromJSON(data.created) ?? undefined,
      readAccess: Guid.fromJSONArray(data.readAccess) ?? undefined,
      writeAccess: Guid.fromJSONArray(data.writeAccess) ?? undefined,
      hasSystemPermissions: Boolean(data.hasSystemPermissions ?? false),
      name: DocumentNameSchema.deserialize(data.name),
      topics: data.topics?.map(DocumentTopicSchema.deserialize),
      templateIds: Guid.fromJSONArray(data.templateIds) ?? undefined,
    }),
    encode: (document) => ({
      id: document.id.toJSON(),
      creatorId: document.creatorId ? document.creatorId.toJSON() : undefined,
      fileSize: document.fileSize,
      fileType: document.fileType.valueOf(),
      created: document.created?.toJSON(),
      readAccess: document.readAccess?.map((guid) => guid.toJSON()),
      writeAccess: document.writeAccess?.map((guid) => guid.toJSON()),
      hasSystemPermissions: document.hasSystemPermissions ?? false,
      name: document.name.toJSON(),
      topics: document.topics?.map(DocumentTopicSchema.serialize),
      templateIds: document.templateIds?.map((guid) => guid.toJSON()),
    }),
  });
}
